import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import Select from "react-select";

import { useFormik } from "formik";
import * as Yup from "yup";

import { Vehicle } from "../../models/vehicle/vehicle";
import { Customer } from "models/customer/customer";
import { CreateVehicle, GetMakes, GetVehicleModels, UpdateVehicle } from "api/vehicle";
import { CreateCustomer, GetCustomers, UpdateCustomer } from "api/customer";
import LoadingComponent from "common/components/loader";
import { GetJobs } from "api/job";
import { Job, JobAllocation, JobService } from "models/job/job";
import TableContainer from "common/table-container";
import { RoutePath } from "routes/route-path";

const validationSchema = Yup.object({
  name: Yup.string().required("Customer name is required"),
  contactNumber: Yup.string().required("Phone is required"),
  email: Yup.string().email("Invalid email format").required("Email is required"),
});

interface Props {
  customer?: Customer;
  onCustomerSaveSuccess?: (customer: Customer) => void;
}

const AddEditCustomerComponent: React.FC<Props> = ({ customer, onCustomerSaveSuccess }) => {
  const navigate = useNavigate();
  // Retrieve query parameters
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const optParam = searchParams.get("opt");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [selectedCustomer, setSelectedCustomer] = useState<Customer | undefined>();
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle>();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [editMode, setEditMode] = useState(false);

  const [makes, setMakes] = useState<{ value: string; label: string }[]>([]);
  const [selectedMake, setSelectedMake] = useState<{ value: string; label: string }>();
  const [allModels, setAllModels] = useState<{ value: string; label: string; parent: string }[]>([]);
  const [filteredModels, setFilteredModels] = useState<{ value: string; label: string; parent: string }[]>([]);
  const [showModelName, setShowModelName] = useState(false);

  // Sample existing customers for autocomplete
  const [existingCustomers, setExistingCustomers] = useState<Customer[]>([]);
  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 50 }, (_, i) => currentYear - i);
  };
  const years = generateYears();

  useEffect(() => {
    loadMakes();
    loadModels();
    // loadCustomers();
  }, []);

  useEffect(() => {
    console.log(customer);
    if (customer) {
      setSelectedCustomer({ ...customer });
      setEditMode(true);
      formik.setValues(customer); // Set formik values when tenant prop changes
    }
    /*  if (vehicle) {
      setSelectedVehicle({ ...vehicle });
      setEditMode(true);
      formik.setValues(vehicle); // Set formik values when tenant prop changes
      formik.setFieldValue(
        "make",
        makes.find((m) => m.value == (vehicle.model?.manufacturer?.id ?? ""))
      );
      const _make = makes.find((m) => m.value == (vehicle.model?.manufacturer?.id ?? ""));
      setSelectedMake(_make);
      formik.setFieldValue(
        "model",
        filteredModels.find((m) => m.value == (vehicle.model?.id ?? ""))
      );

      setSelectedCustomer(vehicle.currentOwner);
      setOwnerType("existing");

      loadJobHistory(vehicle.vehicleNo ?? "");
    } else {
      setEditMode(false);
      setJobs([]);
    } */
  }, [customer]);

  const initialValues = {
    ownerName: selectedCustomer?.name ?? "",
    ownerContactNo: selectedCustomer?.contactNumber ?? "",
    ownerEmail: selectedCustomer?.email ?? "",
    // ownerType: selectedVehicle ? "existing" : "new", // Add ownerType to initial values
    isActive: selectedCustomer?.isActive ?? false,

    vehicleNo: "",
    year: "",
    chassisNo: "",
    make: null,
    model: null,
    modelName: "",
  };
  //TODO:open from existing vehicle from url param
  const formik: any = useFormik({
    initialValues: initialValues,
    validationSchema,
    onSubmit: (values: any) => {
      // console.log(values);
      addEditCustomer(values);
    },
  });

  const loadMakes = async () => {
    const res = await GetMakes();
    if (!(res?.status?.isError ?? false) && res?.data) {
      let _st = (res?.data ?? []).map((t, index) => ({ value: (t.id ?? 0).toString(), label: t.name ?? "" }));
      if (_st) {
        _st = _st.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
      }
      setMakes([{ label: "Select", value: "" }, ..._st]);
    } else {
      setMakes([]);
    }
    if (selectedVehicle) {
      const _make = makes.find((m) => m.value == (selectedVehicle.model?.manufacturer?.id ?? ""));
      setSelectedMake(_make);
      formik.setFieldValue("make", _make);
    }
  };

  const loadModels = async () => {
    const res = await GetVehicleModels();
    if (!(res?.status?.isError ?? false) && res?.data) {
      let _st = (res?.data ?? []).map((t, index) => ({
        value: (t.id ?? 0).toString(),
        label: t.name ?? "",
        parent: (t.manufacturer?.id ?? "").toString(),
      }));
      if (_st) {
        _st = _st.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
      }
      setAllModels(_st);
    } else {
      setAllModels([]);
    }
  };
  /*   const loadCustomers = async () => {
    const res = await GetCustomers();
    if (!(res?.status?.isError ?? false) && res?.data) {
      setExistingCustomers(res?.data);
      if (selectedVehicle && selectedVehicle.currentOwner) {
        setSelectedCustomer(res?.data.find((c) => c.id == selectedVehicle.currentOwner!.id));
      }
    } else {
      setExistingCustomers([]);
    }
  };
 */
  const handleResetClick = () => {
    setSelectedVehicle(undefined);
    setEditMode(false);
    formik.setValues(initialValues); // Set formik values when prop changes
  };

  useEffect(() => {
    if (!selectedMake) {
      const _fm = [...allModels];
      _fm.push({ label: "Other", value: "-1", parent: "" });
      setFilteredModels(_fm);
    } else {
      const _fm = [...allModels.filter((s) => s.parent == selectedMake.value)];
      _fm.push({ label: "Other", value: "-1", parent: "" });
      setFilteredModels(_fm);
    }
  }, [selectedMake]);

  useEffect(() => {
    if (selectedVehicle) {
      formik.setFieldValue(
        "model",
        filteredModels.find((m) => m.value == (selectedVehicle.model?.id ?? ""))
      );
    }
  }, [filteredModels]);

  const addEditCustomer = async (values: any) => {
    console.log();
    try {
      setError("");
      setSuccess("");
      setLoading(true);
      console.log(formik.values);

      var _customer: Customer = {
        id: selectedCustomer?.id ?? "",
        name: values.name ?? "",
        contactNumber: values.contactNumber ?? "",
        email: values.email ?? "",
        isActive: values.isActive ?? false,
        /* vehicleNo: values.vehicleNo,
        year: values.year,
        chassisNo: values.chassisNo,
        currentOwner: {}, */
      };

      /*if (values.model && values.model.value != "-1") {
        _customer.model = {
          id: values.model.value,
          name: values.model.label,
        };
      } else {
        _customer.modelName = values.modelName;
      }
        if (ownerType === "existing" && selectedCustomer) {
        _vehicle.currentOwner!.id = selectedCustomer.id ?? "";
      } else 
      {
        _vehicle.currentOwner!.name = values.ownerName;
        _vehicle.currentOwner!.contactNumber = values.ownerContactNo;
        _vehicle.currentOwner!.email = values.ownerEmail;
      } */
      const res = (_customer?.id ?? "") == "" ? await CreateCustomer(_customer) : await UpdateCustomer(_customer);

      if (!(res?.status?.isError ?? false) && (res?.data?.id ?? "") != "") {
        setSuccess("success");
        console.log(res?.data);
        setSelectedCustomer(res?.data ?? {});
        if (optParam == "n") {
          navigate(`${RoutePath.Admin_Job_create}?c=` + res?.data?.id);
        } else {
          if (onCustomerSaveSuccess) onCustomerSaveSuccess(res?.data ?? {});
        }
      } else {
        setError(`error: ${res?.status?.errorCode ?? "500"}:${res?.status?.errorMessage ?? ""}`);
      }
      setLoading(false);
    } catch (error) {
      setError("Something went wrong...");
      setLoading(false);
    }
    setTimeout(() => {
      setError("");
      setSuccess("");
    }, 3000);
  };

  const columns = useMemo(
    () => [
      {
        header: "Vehicle Number",
        accessorKey: "",
        enableColumnFilter: false,
        cell: (cell: any) => {
          return (
            <>
              <div className="text-sm whitespace-normal">
                <p>Vehicle No: {cell.row.original.id ?? ""}</p>
              </div>
              <p>Chassis No: {cell.row.original.description ?? ""}</p>
            </>
          );
        },
      },

      {
        header: "Model",
        accessorKey: "model",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <>
            {/*     {(cell.row.original.services ?? []).map((itm: JobService, index: number) => (
              <p key={`svc_${index}`}>{itm.service?.name ?? ""}</p>
            ))} */}
          </>
        ),
      },
    ],
    []
  );

  return (
    <>
      {loading && <LoadingComponent />}
      {success && (
        <div
          className="px-4 py-3 mt-3 text-sm text-green-500 border border-green-200 rounded-md bg-green-50 dark:bg-green-400/20 dark:border-green-500/50"
          id="successAlert"
        >
          <b>Success! </b>
          {<>Customer has been {!editMode ? "added" : "updated"} successfully</>}
        </div>
      )}
      {error && (
        <div
          className="px-4 py-3 mt-3 text-sm text-red-500 border border-red-200 rounded-md bg-red-50 dark:bg-red-400/20 dark:border-red-500/50"
          id="successAlert"
        >
          <>
            Oops! Something went wrong while attempting to {!editMode ? "add" : "update"} customer. Please try again
            later or contact support for assistance.
          </>
        </div>
      )}

      <form
        action="#!"
        id="customerForm"
        onSubmit={(event: any) => {
          event.preventDefault();
          formik.handleSubmit();
        }}
      >
        <div className="grid grid-cols-1 gap-x-5">
          <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-2">
            <>
              <div className="mb-4">
                <label htmlFor="name" className="inline-block mb-2 text-base font-medium">
                  Customer Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  className="form-input"
                  placeholder="Customer Name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.name && formik.errors.name ? (
                  <p className="mt-1 text-sm text-red-500">{formik.errors.name}</p>
                ) : null}
              </div>

              <div className="mb-4">
                <label htmlFor="contactNumber" className="inline-block mb-2 text-base font-medium">
                  Contact No <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="contactNumber"
                  className="form-input"
                  placeholder="Customer Contact Number"
                  value={formik.values.contactNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.contactNumber && formik.errors.contactNumber ? (
                  <p className="mt-1 text-sm text-red-500">{formik.errors.contactNumber}</p>
                ) : null}
              </div>

              <div className="mb-4">
                <label htmlFor="email" className="inline-block mb-2 text-base font-medium">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  placeholder="Customer Email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.email && formik.errors.email ? (
                  <p className="mt-1 text-sm text-red-500">{formik.errors.email}</p>
                ) : null}
              </div>
              <div className="mb-4">
                <div>
                  <label htmlFor="isActive" className="inline-block mb-2 text-base font-medium">
                    Active
                  </label>
                  <div className="flex items-center">
                    <div className="relative inline-block w-10 align-middle transition duration-200 ease-in ltr:mr-2 rtl:ml-2">
                      <input
                        type="checkbox"
                        name="isActive"
                        id="isActive"
                        className="absolute block size-5 transition duration-300 ease-linear border-2 rounded-full appearance-none cursor-pointer border-slate-200 dark:border-zink-500 bg-white/80 dark:bg-zink-400 peer/published checked:bg-white dark:checked:bg-white ltr:checked:right-0 rtl:checked:left-0 checked:bg-none checked:border-green-500 dark:checked:border-green-500 arrow-none"
                        onChange={formik.handleChange}
                        checked={formik.values.isActive}
                      />
                      <label
                        htmlFor="isActive"
                        className="block h-5 overflow-hidden duration-300 ease-linear border rounded-full cursor-pointer cursor-pointertransition border-slate-200 dark:border-zink-500 bg-slate-200 dark:bg-zink-600 peer-checked/published:bg-green-500 peer-checked/published:border-green-500"
                      ></label>
                    </div>
                  </div>
                </div>

                {formik.touched.isActive && formik.errors.isActive ? (
                  <p id="isActive" className="mt-1 text-sm text-red-500">
                    {formik.errors.isActive}
                  </p>
                ) : null}
              </div>
            </>
            {/*
            {editMode && (
              <div className="mb-4 xl:col-span-2">
                <h3 className="text-lg font-semibold my-4"> Vehicles</h3>
                {/* Separator * /}
                <hr className="my-3" />
                <div>
                  <div className="overflow-x-auto mt-2">
                    {jobs && jobs.length > 0 ? (
                      <TableContainer
                        isPagination={true}
                        columns={columns || []}
                        data={jobs || []}
                        totalCount={1000}
                        customPageSize={1000}
                        divclassName="overflow-x-auto"
                        tableclassName="w-full whitespace-nowrap"
                        theadclassName="ltr:text-left rtl:text-right bg-slate-100 text-slate-500 dark:bg-zink-600 dark:text-zink-200"
                        thclassName="px-3.5 py-2.5 font-semibold border-b border-slate-200 dark:border-zink-500"
                        tdclassName="px-3.5 py-2.5 border-y border-slate-200 dark:border-zink-500"
                        PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
                      />
                    ) : (
                      <div className="noresult">
                        <div className="py-6 text-center">
                          <h5 className="mt-2 mb-1">Sorry! No Result Found</h5>
                          <p className="mb-0 text-slate-500 dark:text-zink-200"></p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}*/}
            {/*
            <div className="mb-4 xl:col-span-2">
              <h3 className="text-lg font-semibold mb-4">Add New Vehicle</h3>
             
              <hr className="my-3" />
            </div>

            <div className="mb-4">
              <label htmlFor="vehicleNo" className="inline-block mb-2 text-base font-medium">
                Vehicle Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="vehicleNo"
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Vehicle Number"
                value={formik.values.vehicleNo}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.vehicleNo && formik.errors.vehicleNo ? (
                <p id="error-vehicleNo" className="mt-1 text-sm text-red-500">
                  {formik.errors.vehicleNo}
                </p>
              ) : null}
            </div>
            <div className="mb-4">
              <label htmlFor="chassisNo" className="inline-block mb-2 text-base font-medium">
                Chassis Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="chassisNo"
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Chassis Number"
                value={formik.values.chassisNo}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.chassisNo && formik.errors.chassisNo ? (
                <p id="error-chassisNo" className="mt-1 text-sm text-red-500">
                  {formik.errors.chassisNo}
                </p>
              ) : null}
            </div>

            <div className="mb-4">
              <label htmlFor="make" className="inline-block mb-2 text-base font-medium">
                Make <span className="text-red-500"></span>
              </label>
              <Select
                className="border-slate-200 focus:outline-none focus:border-custom-500"
                options={makes}
                isSearchable={false} // To disable search
                name="make"
                id="make"
                value={formik.values.make} // Set Formik's `serviceType` value
                onChange={(option) => {
                  setSelectedMake(option);
                  formik.setFieldValue("make", option);
                }} // Update Formik state
              />
              {formik.touched.make && formik.errors.make ? (
                <p id="error-make" className="mt-1 text-sm text-red-500">
                  {formik.errors.make.label || formik.errors.make}
                </p>
              ) : null}
            </div>
            <div className="mb-4">
              <label htmlFor="year" className="inline-block mb-2 text-base font-medium">
                Year <span className="text-red-500">*</span>
              </label>
              <select
                name="year"
                className="form-select"
                value={formik.values.year}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <option value="">Select Year</option>
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              {formik.touched.year && formik.errors.year ? (
                <p className="mt-1 text-sm text-red-500">{formik.errors.year}</p>
              ) : null}
            </div>
            <div className="mb-4">
              <label htmlFor="model" className="inline-block mb-2 text-base font-medium">
                Model <span className="text-red-500"></span>
              </label>
              <Select
                className="border-slate-200 focus:outline-none focus:border-custom-500"
                options={filteredModels}
                isSearchable={false} // To disable search
                name="model"
                id="model"
                value={formik.values.model} // Set Formik's `serviceType` value
                onChange={(option) => {
                  formik.setFieldValue("model", option);
                  setShowModelName((option?.value ?? "") == "-1");
                }} // Update Formik state
              />
              {formik.touched.make && formik.errors.model ? (
                <p id="error-model" className="mt-1 text-sm text-red-500">
                  {formik.errors.model.label || formik.errors.model}
                </p>
              ) : null}
            </div>
            {showModelName && (
              <div className="mb-4">
                <label htmlFor="modelName" className="inline-block mb-2 text-base font-medium">
                  Model Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="modelName"
                  className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="Model Name"
                  value={formik.values.modelName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.modelName && formik.errors.modelName ? (
                  <p id="error-modelName" className="mt-1 text-sm text-red-500">
                    {formik.errors.modelName}
                  </p>
                ) : null}
              </div>
            )}
*/}
            {/*   <div className="mb-4 xl:col-span-2">
              <label className="inline-block mb-2 text-base font-medium">Owner Type</label>
              <div className="flex items-center">
                <label className="mr-4">
                  <input
                    type="radio"
                    name="ownerType"
                    value="existing"
                    checked={ownerType === "existing"}
                    onChange={() => {
                      setOwnerType("existing");
                      setSelectedCustomer(undefined); // Reset selected customer
                    }}
                  />
                  <span className="px-2">Existing Customer</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="ownerType"
                    value="new"
                    checked={ownerType === "new"}
                    onChange={() => setOwnerType("new")}
                  />
                  <span className="px-2">New Customer</span>
                </label>
              </div>
            </div> */}

            {/* {ownerType === "existing" && (
              <div className="mb-4">
                <label htmlFor="existingCustomer" className="inline-block mb-2 text-base font-medium">
                  Select Existing Customer
                </label>
                <input
                  type="text"
                  list="customerList"
                  placeholder="Start typing..."
                  value=""
                  onChange={(e) => {
                    const query = e.target.value;
                    if (query != "") {
                      const matchedCustomer = existingCustomers.find((customer) =>
                        (customer.name ?? "").toLowerCase().includes(query.toLowerCase())
                      );
                      setSelectedCustomer(matchedCustomer);
                    } else {
                      setSelectedCustomer(undefined);
                    }
                  }}
                  className="form-input"
                />
                <datalist id="customerList">
                  {existingCustomers.map((customer) => (
                    <option key={customer.id} value={customer.name} />
                  ))}
                </datalist>
                {selectedCustomer && (
                  <div className="mt-2">
                    <p>
                      <strong>Name:</strong> {selectedCustomer.name ?? ""}
                    </p>
                    <p>
                      <strong>Contact No:</strong> {selectedCustomer.contactNumber ?? ""}
                    </p>
                    <p>
                      <strong>Email:</strong> {selectedCustomer.email ?? ""}
                    </p>
                  </div>
                )}
              </div>
            )} */}

            {/*   {ownerType === "new" && ( */}

            <div className="mb-4"></div>
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <button
              type="button"
              onClick={handleResetClick}
              className="text-red-500 bg-white btn hover:text-red-500 hover:bg-red-100 focus:text-red-500 focus:bg-red-100 active:text-red-500 active:bg-red-100 dark:bg-zink-700 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10 dark:active:bg-red-500/10"
            >
              <X className="inline-block size-4" /> <span className="align-middle">Reset</span>
            </button>
            <button
              type="submit"
              className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
            >
              {(selectedCustomer?.id ?? "") != "" ? "Update" : "Add"} Customer
            </button>
            {(selectedCustomer?.id ?? "") != "" && (
              <button
                type="button"
                onClick={() => {
                  navigate(`${RoutePath.Admin_Job_create}?c=` + selectedCustomer?.id);
                }}
                className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
              >
                Create a Job
              </button>
            )}
          </div>
        </div>
      </form>
    </>
  );
};

export default AddEditCustomerComponent;
